<script>
import debounce from 'lodash/debounce'
import axios from 'axios'

export default {
  inheritAttrs: false,
  model: {
    event: 'update',
  },
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      placeSuggestions: [],
      focusedPlaceIndex: 0,
      localValue: this.value || '',
    }
  },
  computed: {
    focusedPlace() {
      return this.placeSuggestions[this.focusedPlaceIndex]
    },
  },
  watch: {
    value() {
      this.localValue = this.value || ''
    },
  },
  created() {
    this.debouncedUpdatePlaceSuggestions = debounce(
      this.updatePlaceSuggestions,
      500
    )
  },
  methods: {
    updatePlaceSuggestions() {
      if (!this.localValue.trim()) return

      axios
        .get('/api/places', {
          params: {
            text: this.localValue,
          },
        })
        .then((response) => {
          this.placeSuggestions = response.data.places
          this.focusedPlaceIndex = 0
        })
    },
    updateLocalValue(queryText) {
      this.$emit('update:coords', null)
      this.localValue = queryText
      this.debouncedUpdatePlaceSuggestions()
    },
    selectPlace() {
      const place = this.focusedPlace

      if (!place) return

      this.localValue = place.description
      this.$emit('update', place.description)

      axios.get(`/api/places/${place.place_id}`).then((response) => {
        const { lat, lng } = response.data.place.geometry.location
        this.$emit('update:coords', {
          latitude: lat,
          longitude: lng,
          place,
        })
      })

      this.$refs.baseInput.$refs.input.blur()
    },
    focusPrevPlace() {
      this.focusedPlaceIndex =
        this.focusedPlaceIndex > 0
          ? this.focusedPlaceIndex - 1
          : this.placeSuggestions.length - 1
    },
    focusNextPlace() {
      this.focusedPlaceIndex =
        this.focusedPlaceIndex < this.placeSuggestions.length - 1
          ? this.focusedPlaceIndex + 1
          : 0
    },
  },
}
</script>

<template>
  <div :class="$style.container">
    <BaseInput
      ref="baseInput"
      v-bind="$attrs"
      :class="$style.input"
      :value="localValue"
      @update="updateLocalValue"
      @focus="$event.target.select()"
      @blur="placeSuggestions = []"
      @keyup.up.prevent="focusPrevPlace"
      @keydown.down.prevent="focusNextPlace"
      @keydown.enter.prevent="selectPlace"
    />
    <ul v-if="placeSuggestions.length" :class="$style.suggestions">
      <li
        v-for="(place, placeIndex) in placeSuggestions"
        :key="place.id"
        :class="[
          $style.suggestion,
          { [$style.focused]: placeIndex === focusedPlaceIndex },
        ]"
        @mouseenter="focusedPlaceIndex = placeIndex"
        @mousedown="selectPlace"
        >{{ place.description }}</li
      >
    </ul>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.container {
  position: relative;
  margin-bottom: $size-grid-padding;
}

.input.input {
  margin-bottom: 0;
}

.suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1;
  width: 100%;
  padding: 0;
  margin: 0;
  list-style: none;
  background: white;
  border: $size-input-border solid $color-input-border;
  border-top: none;
  border-bottom-right-radius: $size-input-border-radius;
  border-bottom-left-radius: $size-input-border-radius;
  box-shadow:
    0 2px 2px 0 rgba(0, 0, 0, 0.16),
    0 0 0 1px rgba(0, 0, 0, 0.08);
}

.suggestion {
  padding: $size-grid-padding * 0.5;
  cursor: pointer;

  &.focused {
    background: #f9f7f5;
  }
}
</style>
